import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { StratosCardLink } from "../../../../modules/content/components/StratosCardLink/StratosCardLink";
import { StratosCommonContent } from "../../../../modules/content/components/StratosCommonContent/StratosCommonContent";
import { StratosRichText } from "../../../../modules/content/components/StratosRichText/StratosRichText";
import { StratosSection } from "../../../../modules/content/components/StratosSection/StratosSection";
import type { IIconContentGridBlockProps } from "./IconContentGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;

const surfaces = ["bg-mist", "bg-sunlit", "bg-deep-violet text-cloud-white"] as const;

export const IconContentGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: IIconContentGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <StratosSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <StratosCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2", columns[columnCount || 3])}>
          {items.map((item, index) => (
            <StratosCardLink
              className={cn(
                "min-h-72 rounded-[20px] border-0 p-7 md:p-9",
                surfaces[index % surfaces.length],
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <Icon className="size-11 text-primary" name={item.icon} />
              <h3
                className={cn(
                  "mt-16 max-w-[16ch] font-stratos-display text-3xl font-bold leading-[0.95] tracking-[-0.03em]",
                  index % surfaces.length === 2 ? "text-cloud-white" : "text-night-flight",
                )}
              >
                {item.heading}
              </h3>
              {item.description ? (
                <StratosRichText
                  className={cn(
                    "mt-4 text-base md:text-lg",
                    index % surfaces.length === 2 ? "text-cloud-white/75" : "text-night-flight/70",
                  )}
                  content={item.description}
                />
              ) : null}
            </StratosCardLink>
          ))}
        </div>
      </div>
    </StratosSection>
  );
};

IconContentGridBlock.displayName = "Block::IconContentGridBlock";
